import { faWeightScale } from "@fortawesome/free-solid-svg-icons";
import { Chart, ChartData, CustomTooltipProps, TitleStyle, TooltipContainer, TooltipText } from "./Chart";
import { useMemo } from "react";
import { DateTime, Interval } from "luxon";
import { Guard } from "../utils";
import _ from "lodash";
import { WeightUnit, getWeightKg } from "../quiz/weight";

const CustomTooltip = ({ active, payload }: CustomTooltipProps): JSX.Element => {
  if (active && payload && payload.length) {
    const weight = payload[0].value as number
    return (
      <TooltipContainer>
        <TooltipText>{`Weight: ${weight.toPrecision(4)} lbs`}</TooltipText>
      </TooltipContainer>
    );
  } else {
    return <></>;
  }
};

type WeightLossChartData = ChartData & {
  bodyWeightLbs: number;
};

const isWeightLossData: Guard<WeightLossChartData> = (data: unknown): data is WeightLossChartData => {
  return (data as WeightLossChartData).bodyWeightLbs !== undefined;
};

type WeightLossChartProps = {
  data: WeightLossChartData[];
  showTitle?: TitleStyle;
  weightUnit: WeightUnit;
  timeToGoal: Interval;
  showOnlyTitle?: boolean;
  showBigHeader?: boolean;
}

export function getWeightLossPredictionData({
  data, showTitle, weightUnit,timeToGoal
} : {
  data: WeightLossChartData[];
  showTitle: TitleStyle | undefined;
  weightUnit: WeightUnit;
  timeToGoal: Interval;
}) {
  if (data != null && data.length > 0) {
    const hideYear = data[data.length - 1].date.year === DateTime.local().year;
    const predictedDate = _.last(data)!.date.toFormat(`MMMM d${hideYear ? '' : ' yyyy'}`);
    const predictedDatetoGoal=timeToGoal.end.toFormat(`MMMM d${hideYear ? '' : ' yyyy'}`);
    const titlePrefix = TitleStyle.Text === showTitle ? 'You can reach' : `YOUR WEIGHT LOSS`;
    const lastDataPoint = _.last(data)!.bodyWeightLbs;
    const endWeight = weightUnit === WeightUnit.Pounds ? lastDataPoint : getWeightKg(lastDataPoint, WeightUnit.Pounds);
    const titlePrediction = timeToGoal.toDuration('weeks').weeks <= 3 ? `${endWeight} ${weightUnit} by`: `${endWeight} ${weightUnit} by`;
    const titlePredictionDate = timeToGoal.toDuration('weeks').weeks <= 3 ? predictedDatetoGoal : predictedDate;
    
    return {
      titlePrefix,
      titlePrediction,
      titlePredictionDate,
      finalMarker: `${titlePrediction} ${titlePredictionDate}`,
    };
  } 
  return null;
}

const WeightLossChart = (props: WeightLossChartProps) => {
  const { data, showTitle, weightUnit,timeToGoal, showOnlyTitle, showBigHeader } = props;

  const predictionData = useMemo(() => {
    return getWeightLossPredictionData({ data, showTitle, weightUnit, timeToGoal });
  }, [data, showTitle, weightUnit,timeToGoal]);

  if (predictionData) {
    const { titlePrefix, titlePrediction, titlePredictionDate } = predictionData;

    return <Chart
      data={data.map((d) => ({ ...d, bodyWeight: weightUnit === WeightUnit.Kilograms ? getWeightKg(d.bodyWeightLbs, WeightUnit.Pounds) : d.bodyWeightLbs }))}
      dataKey="bodyWeight"
      showTitle={showTitle}
      icon={faWeightScale}
      CustomTooltip={CustomTooltip}
      titlePrefix={titlePrefix}
      titlePrediction={titlePrediction}
      titlePredictionDate={titlePredictionDate}
      finalMarker={`${titlePrediction} ${titlePredictionDate}`}
      caret={'▼'}
      markerLocation="bottom"
      yAxisLabel={`Weight (${weightUnit})`}
      showOnlyTitle={showOnlyTitle}
      showBigHeader={showBigHeader}
    />;
  } else {
    return <></>;
  }
};

export {
  WeightLossChart,
  isWeightLossData,
};
export type { WeightLossChartProps, WeightLossChartData };
